import { useSyncedRef } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import type { NextPageContext, PageProps } from "next";
import type { MouseEvent } from "react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponentWithLayout } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { useNewsletterApi } from "scmp-sdk";
import "swiper/css";
import "swiper/css/free-mode";

import { useKeywordMetaTags } from "shared/components/meta/hooks";
import { config } from "shared/data";
import { NEWSLETTER_BUNDLES } from "shared/data/newsletter";
import { useResponsive } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { AppFooter } from "scmp-app/components/app-footer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { PageSeo } from "scmp-app/components/meta";
import { signUpActionRedirectAtom } from "scmp-app/components/popup-container/sign-up-action-redirect/atoms";
import { trackingAtom } from "scmp-app/components/tracking";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { getPymChild, usePymJs } from "scmp-app/lib/pym/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { newslettersPageQuery } from "scmp-app/queries/__generated__/newslettersPageQuery.graphql";

import { NewsletterLandingContextProvider } from "./contexts";
import { getPackageNameFromNewsletterIds } from "./helpers";
import {
  Category,
  CategoryListContainer,
  CategorySwiper,
  CategorySwiperContainer,
  CategorySwiperSlide,
  Container,
  Heading,
  ManageNotificationLink,
  NewsletterCategorySlideshowContainer,
  StyledBundledNewsletterCategorySlideshow,
  StyledIconBell,
  StyledNewsletterCategorySlideshow,
  StyledNewsletterColumnistSlideshow,
  SubHeading,
} from "./styles";
import type { NewsLetterSignUpButtonAction, NewsLetterUnsubscribePayload } from "./types";

const query = graphql`
  query newslettersPageQuery($bundledNewslettersEntityIds: [String!]) {
    newsletterCategories(first: 100, orderBy: { field: WEIGHT, direction: DESC }) {
      edges {
        node {
          entityId
          name
          ...newsletterCategoryItemSlideshowNewsletterCategory
        }
      }
    }
    ...bundledNewsletterCategoryItemSlideshowNewsletterCategory
      @arguments(bundledNewslettersEntityIds: $bundledNewslettersEntityIds)
    ...newsletterColumnistSlideshowAppConfigQuery
  }
`;

const NewslettersPage: RelayComponentWithLayout<PageProps, newslettersPageQuery> = ({
  preloadedQuery,
  ...pageProps
}) => {
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  const queryReference = usePreloadedQuery(query, preloadedQuery);
  const isEmbed = pageProps.appInitializerConfiguration?.theme === "embed";
  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);
  const keywordTags = useKeywordMetaTags("newsletters");
  const { isLoggedIn } = useAtomValue(accountAtom);
  const { ready: isSignUpActionRedirectReady } = useAtomValue(signUpActionRedirectAtom);
  const { captureElement } = usePymJs(isEmbed);
  const { openLoginDialog } = useLoginDialogStateHelper();
  const manageNotificationUrl = `${config.account.scmpAccountHost}/manage/notifications`;

  const handleManageNotificationClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (isLoggedIn) return;
      event.preventDefault();
      openLoginDialog({ destination: manageNotificationUrl });
    },
    [isLoggedIn, manageNotificationUrl, openLoginDialog],
  );

  // Redirect logged-in users to accounts settings page, except in embed mode
  useEffect(() => {
    if (!isLoggedIn || !isSignUpActionRedirectReady || isEmbed) return;
    const url = new URL(`${config.account.scmpAccountHost}/manage/newsletter`);
    window.location.href = url.toString();
  }, [isLoggedIn, isEmbed, isSignUpActionRedirectReady]);

  const handleCategoryClick = useCallback(
    (elementId: string) => {
      if (typeof window === "undefined") return;
      const target = document.querySelector(elementId) as HTMLElement;
      if (!notEmpty(target)) return;

      if (isEmbed) {
        getPymChild().sendMessage(
          "SCROLL_REQUEST",
          JSON.stringify({ scrollTop: target.offsetTop + 20 }),
        );
        return;
      }

      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    [isEmbed],
  );

  const renderNewsletterCategories = useCallback(() => {
    const categories = (queryReference?.newsletterCategories?.edges ?? []).map(
      ({ node: { entityId, name } }) => ({
        elementId: `#newsletterCategorySlideshow-${entityId}`,
        name,
      }),
    );
    // insert special categories
    categories.splice(0, 0, {
      elementId: "#newsletterBundleSlideshow",
      name: "Newsletter Bundles",
    });
    categories.splice(7, 0, { elementId: "#newsletterColumnistSlideshow", name: "Follow Alerts" });

    return categories.map(({ elementId, name }, index) => (
      <CategorySwiperSlide key={index}>
        <Category onClick={() => handleCategoryClick(elementId)}>{name}</Category>
      </CategorySwiperSlide>
    ));
  }, [queryReference?.newsletterCategories?.edges, handleCategoryClick]);

  const [unsubscribePayload, setUnsubscribePayload] = useState<
    NewsLetterUnsubscribePayload | undefined
  >();
  const [isMountedListener, setIsMountedListener] = useState(false);
  const { handleUnsubscribeNewsletter } = useNewsletterApi();

  const newsletterHandler = useCallback(
    async (data: string) => {
      if (!isEmbed) return;
      const { type } = JSON.parse(data) as NewsLetterSignUpButtonAction;

      switch (type) {
        case "UNSUBSCRIBE_CANCELLED":
          break;
        case "UNSUBSCRIBE_CONFIRMED":
          if (!notEmpty(unsubscribePayload)) break;

          const { dataMatrix, email, newsletterIds, newsletterNames } = unsubscribePayload;
          await handleUnsubscribeNewsletter(newsletterIds, email, dataMatrix);
          const packageName = getPackageNameFromNewsletterIds(newsletterIds);

          sendGA4Tracking({
            action: "sys",
            category: "nl",
            customized_parameters: {
              newsletter_name: newsletterNames.join("|"),
              page_name: "my_account",
              ...(packageName && { package_name: packageName }),
            },
            subcategory: "unsub",
          });
          break;
      }
      setUnsubscribePayload(undefined);
    },
    [handleUnsubscribeNewsletter, isEmbed, unsubscribePayload],
  );
  const syncedNewsletterHandler = useSyncedRef(newsletterHandler);

  useEffect(() => {
    if (isMountedListener || !isEmbed) return;
    getPymChild().onMessage("UNSUBSCRIBE_RESPONSE", (data: string) => {
      void syncedNewsletterHandler.current(data);
    });
    setIsMountedListener(true);
  }, [isEmbed, isMountedListener, syncedNewsletterHandler]);

  return (
    <NewsletterLandingContextProvider
      ga4CustomParameter={{ triggerPoint: "newsletter index" }}
      isEmbed={isEmbed}
      setUnsubscribePayload={setUnsubscribePayload}
    >
      <PageSeo
        additionalMetaTags={[...keywordTags]}
        openGraph={{ title: "Newsletters | South China Morning Post" }}
        title="Newsletters"
      />
      <Container $isEmbed={isEmbed} ref={captureElement}>
        <Heading>Sign up for our Newsletters</Heading>
        <SubHeading>
          The best of the South China Morning Post, delivered to your inbox for free
        </SubHeading>
        <ManageNotificationLink
          href={manageNotificationUrl}
          onClick={handleManageNotificationClick}
          rel="noreferrer"
          target="_top"
        >
          <StyledIconBell />
          Manage notification
        </ManageNotificationLink>
        {isTabletUp ? (
          <CategoryListContainer>{renderNewsletterCategories()}</CategoryListContainer>
        ) : (
          <CategorySwiperContainer>
            <CategorySwiper
              slidesOffsetAfter={10}
              slidesOffsetBefore={20}
              slidesPerView="auto"
              spaceBetween={8}
            >
              {renderNewsletterCategories()}
            </CategorySwiper>
          </CategorySwiperContainer>
        )}

        <NewsletterCategorySlideshowContainer>
          <StyledBundledNewsletterCategorySlideshow
            id="newsletterBundleSlideshow"
            reference={queryReference}
          />
        </NewsletterCategorySlideshowContainer>

        {queryReference?.newsletterCategories?.edges.map((edge, index) => (
          <Fragment key={index}>
            <NewsletterCategorySlideshowContainer $isEmbed={isEmbed} key={edge.node.entityId}>
              <StyledNewsletterCategorySlideshow
                id={`newsletterCategorySlideshow-${edge.node.entityId}`}
                reference={edge.node}
              />
            </NewsletterCategorySlideshowContainer>
            {index === 5 && (
              <NewsletterCategorySlideshowContainer>
                <StyledNewsletterColumnistSlideshow
                  id="newsletterColumnistSlideshow"
                  reference={queryReference}
                />
              </NewsletterCategorySlideshowContainer>
            )}
          </Fragment>
        ))}
      </Container>

      {!isEmbed && <AppFooter />}
    </NewsletterLandingContextProvider>
  );
};

NewslettersPage.displayName = "NewslettersPage";

const getPageProps = (context: NextPageContext) => {
  const isEmbedMode = notEmpty(context.query.embed);

  const pageProps: PageProps = {
    appInitializerConfiguration: {
      theme: isEmbedMode ? "embed" : "default",
    },
  };

  if (isEmbedMode) {
    pageProps.pageConfiguration = {
      ...pageProps.pageConfiguration,
      responsivePadding: {
        desktopUp: 0,
        mobileUp: 0,
        tabletUp: 0,
      },
    };
    pageProps.contentConfiguration = {
      ...pageProps.contentConfiguration,
      responsivePadding: {
        desktopUp: 0,
        mobileUp: 0,
        tabletUp: 0,
      },
    };
  }
  return pageProps;
};

export default withRelay(NewslettersPage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext: () => ({
    bundledNewslettersEntityIds: [
      ...new Set(Object.values(NEWSLETTER_BUNDLES).flatMap(({ newsletters }) => newsletters)),
    ],
  }),
});
